<template>
  <v-dialog
    v-model="dialog"
    :max-width="period && !period.paid && getPendingOrder(period) ? 600 : 400"
    scrollable
    :fullscreen="$vuetify.breakpoint.xsOnly"
    transition="dialog-bottom-transition"
  >
    <v-card>
      <v-row v-if="isLoading" class="fill-height" align="center" justify="center" no-gutters style="min-height: 200px">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-row>
      <template v-else-if="period">
        <template v-if="$vuetify.breakpoint.smAndUp">
          <v-toolbar
            elevation="0"
            dense
            dark
            :class="{
              red: period.type === 'stop' || isSuspended,
              green: period.type === 'activation' && !isSuspended,
              blue: period.type === 'swap'
            }"
          >
            <v-spacer></v-spacer>

            <v-btn icon @click="dialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-title
            v-if="$vuetify.breakpoint.smAndUp"
            class="headline white--text pt-0"
            :class="{
              red: period.type === 'stop' || isSuspended,
              green: period.type === 'activation' && !isSuspended,
              blue: period.type === 'swap'
            }"
            >{{
              isSuspended
                ? $t('message.suspended')
                : period.type === 'stop'
                ? $t('message.stopped')
                : period.type === 'swap'
                ? $t('message.swap')
                : $t('message.active')
            }}</v-card-title
          >
        </template>

        <v-toolbar
          v-else
          elevation="0"
          dark
          :class="{
            red: period.type === 'stop' || isSuspended,
            green: period.type === 'activation' && !isSuspended
          }"
          style="max-height: 56px;"
        >
          <v-btn icon dark @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{
            isSuspended
              ? $t('message.suspended')
              : period.type === 'stop'
              ? $t('message.stopped')
              : period.type === 'swap'
              ? $t('message.swap')
              : $t('message.active')
          }}</v-toolbar-title>
        </v-toolbar>

        <v-card-text>
          <v-container>
            <v-row no-gutters justify="start" align="start" class="mt-3 mb-4">
              <v-col cols="auto" class="mr-4">
                <v-icon class="grey--text">mdi-clock-outline</v-icon>
              </v-col>
              <v-col>
                <span>
                  {{ $t('message.from') }} {{ period.startsAt | moment('LL') }}
                  <template v-if="period.endsAt">
                    <br />
                    {{ $t('message.until') }} {{ period.endsAt | moment('LL') }}
                  </template>
                </span>
              </v-col>
            </v-row>
            <v-row v-if="period.agreement.servicePlan" no-gutters justify="start" align="start" class="mb-4">
              <v-col cols="auto" class="mr-4">
                <v-icon class="grey--text">mdi-palette-swatch</v-icon>
              </v-col>
              <v-col>
                <span>{{ period.agreement.servicePlan.name }}</span>
              </v-col>
            </v-row>
            <v-row v-if="period.airtimePlan" no-gutters justify="start" align="start" class="mb-4">
              <v-col cols="auto" class="mr-4">
                <v-icon class="grey--text">mdi-swap-vertical</v-icon>
              </v-col>
              <v-col>
                <span>
                  {{ period.airtimePlan.name }}
                  <br />
                  <span class="caption grey--text">
                    <template v-if="period.airtimePlan.downloadMir && period.airtimePlan.uploadMir">
                      <v-icon class="body-1">mdi-arrow-down</v-icon>
                      {{ period.airtimePlan.downloadMir }} kbps
                      <v-icon class="body-1">mdi-arrow-up</v-icon>
                      {{ period.airtimePlan.uploadMir }} kbps
                    </template>
                    <template v-if="period.airtimePlan.monthlyVolume">
                      <v-icon class="body-1">mdi-cloud-download-outline</v-icon>
                      {{ period.airtimePlan.monthlyVolume }} kbps
                    </template>
                  </span>
                </span>
              </v-col>
            </v-row>
            <v-row v-if="period.comments" no-gutters justify="start" align="start" class="mb-4">
              <v-col cols="auto" class="mr-4">
                <v-icon class="grey--text">mdi-text</v-icon>
              </v-col>
              <v-col>
                <span>{{ period.comments }}</span>
              </v-col>
            </v-row>
            <v-row v-if="period.price" no-gutters justify="start" align="start" class="mb-4">
              <v-col cols="auto" class="mr-4">
                <v-icon class="mr-4 grey--text">mdi-wallet</v-icon>
              </v-col>
              <v-col>
                <span>
                  {{ period.price }} €
                  <br />
                  <span class="caption grey--text"
                    >{{
                    <template v-if="'es' === $i18n.locale">
                      {{ period.pricingDescriptionEs }}
                    </template>
                    <template v-else>
                      {{ period.pricingDescription }}
                    </template>
                    }}</span
                  >
                </span>
              </v-col>
            </v-row>

            <v-card-actions v-if="'stop' === period.type && new Date(period.startsAt) > new Date()">
              <v-btn color="red" text @click="cancelStopDialog = true">{{ $t('message.cancel_stop') }}</v-btn>

              <v-dialog v-model="cancelStopDialog" max-width="400">
                <v-card>
                  <v-card-title class="headline">{{ $t('message.stop_cancel_confirm_title') }}</v-card-title>

                  <v-card-text
                    v-html="
                      $t('message.stop_cancel_confirm', {
                        startsAt: $moment(period.startsAt).format('LL'),
                        siteName: period.agreement.site.name,
                        servicePlanName: period.agreement.servicePlan.name
                      })
                    "
                  ></v-card-text>

                  <v-card-actions>
                    <v-spacer></v-spacer>

                    <v-btn color="grey" text @click="cancelStopDialog = false">{{ $t('message.keep') }}</v-btn>

                    <v-btn color="orange" text @click.stop="deletePeriod(period)">{{
                      $t('message.cancel_stop')
                    }}</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-card-actions>

            <template v-if="period && !period.paid && getPendingOrder(period)">
              <PaymentDetails :order="getPendingOrder(period)" />
              <div class="mt-3">
                <v-btn
                  :to="{
                    name: 'order',
                    params: { id: $iriToId(getPendingOrder(period)['@id']) }
                  }"
                  color="orange"
                  dark
                  >{{ $t('message.pay_now') }}</v-btn
                >
                <v-btn
                  color="red"
                  text
                  :to="{
                    name: 'order',
                    params: { id: $iriToId(getPendingOrder(period)['@id']) }
                  }"
                  >{{ $t('message.cancel_order') }}</v-btn
                >
              </div>
            </template>
          </v-container>
        </v-card-text>
      </template>
    </v-card>
  </v-dialog>
</template>
<script>
import fetch from '@/utils/fetch'
import PaymentDetails from '@/components/PaymentDetails'

export default {
  name: 'PeriodDialog',

  components: {
    PaymentDetails
  },

  props: {
    selectedPeriod: {
      type: Object,
      required: false,
      default: null
    },
    isSuspended: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data() {
    return {
      period: null,
      dialog: false,
      isLoading: false,
      cancelStopDialog: false
    }
  },

  watch: {
    selectedPeriod: {
      handler: function(selectedPeriod) {
        if (!selectedPeriod) {
          return
        }
        this.fetchPeriod(this.selectedPeriod['@id'])
        this.dialog = true
      },
      immediate: true
    },

    dialog: function(dialog) {
      if (false === dialog) {
        this.$emit('close')
      }
    }
  },

  methods: {
    fetchPeriod: function(periodId) {
      this.period = null
      this.isLoading = true
      fetch(periodId)
        .then(response => {
          return response.json()
        })
        .then(data => {
          this.period = data
          this.isLoading = false
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.log(e)
          this.isLoading = false
        })
    },

    deletePeriod: function(period) {
      this.isLoading = true
      fetch(period['@id'], { method: 'DELETE' })
        .then(() => {
          this.isLoading = false
          this.$emit('periodDeleted')
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.log(e)
          this.isLoading = false
        })
        .then(() => {
          this.dialog = false
          this.cancelStopDialog = false
        })
    },

    isPaymentPending(period) {
      if (!period || 'recurring' === period.agreement.servicePlan.type) {
        return false
      }
      if ('on_demand' === period.agreement.servicePlan.type) {
        let donePayments = []
        if (
          period.orderLines.length > 0 &&
          period.orderLines[0] &&
          period.orderLines[0].parentOrder &&
          period.orderLines[0].parentOrder.payments.length > 0
        ) {
          donePayments = period.orderLines[0].parentOrder.payments.filter(payment => {
            return 'done' === payment.status
          })
        }
        return donePayments.length === 0
      }

      if ('fixed_term' === period.agreement.servicePlan.type) {
        let currentMonthPaid = false
        period.orderLines.forEach(orderLine => {
          if (this.$moment(orderLine.parentOrder.issuedAt).isSame(this.$moment(), 'month')) {
            orderLine.parentOrder.payments.forEach(payment => {
              if ('done' === payment.status) {
                currentMonthPaid = true
              }
            })
          }
        })

        return !currentMonthPaid
      }
    },

    getPendingOrder(period) {
      if (!this.isPaymentPending(period)) {
        return
      }

      if ('on_demand' === period.agreement.servicePlan.type) {
        return period.orderLines[0].parentOrder
      }

      if ('fixed_term' === period.agreement.servicePlan.type) {
        let pendingOrder = null
        period.orderLines.forEach(orderLine => {
          if (this.$moment(orderLine.parentOrder.issuedAt).isSame(this.$moment(), 'month')) {
            pendingOrder = orderLine.parentOrder
          }
        })

        return pendingOrder
      }
    }
  }
}
</script>
